import React, { Fragment } from 'react'
import {
  LegacyPolicyPanel,
  LegacyPolicyPanelBody
} from '../../legacy-policy-panel'
import { PolicyManagementFormHeader } from '../../legacy-policy-panel/policy-management-form-header'
import { updateRoute } from '../../route'
import { css } from '@emotion/css'
import {
  VulnerabilityPolicyForm,
  normalizeApplicationsTyped,
  normalizeImportanceTyped,
  defaultDaysOnDisabled
} from './form'
import {
  VulnerabilityPolicy,
  ruleSeveritiesi18nConfig,
  VulnerabilityPolicyAction
} from '../'
import { BaseAssessRule } from '../../assess-rules'
import {
  appImportanceI18n,
  ApplicationImportance
} from '../../application-importance'
import { UpdateVulnerabilityPolicyArgs } from './edit-policy-mutation'
import {
  normalizeAssessRulesToTypedVulnerabilities,
  normalizeRuleSeveritiesToTypedVulnerabilities,
  RuleSeverity
} from '../../shared'
import { ContrastFormattedMessage } from '../../contrast-formatted-message'
import { IconRemediation } from '@contrast-security-inc/design-system-foundations'
import { Button } from '@contrast-security-inc/design-system-buttons'

const routeToSourceNameGrid = (
  organizationID: string,
  action: VulnerabilityPolicyAction
) =>
  updateRoute(
    `/${organizationID}/rules/vulnerability-management?action=${action}`
  )

export interface Props {
  readonly intl: any
  readonly organizationID: string
  readonly vulnerabilityPolicy: VulnerabilityPolicy
  readonly baseAssessRules: BaseAssessRule[]
  readonly vulnAutoVerificationEnabled: boolean
  readonly updateVulnerabilityPolicy: (
    variables: UpdateVulnerabilityPolicyArgs,
    successMessage: string,
    errorMessage: string
  ) => Promise<any>
}

function normalizeImportances(
  applicationImportance: ApplicationImportance[],
  intl
) {
  return normalizeImportanceTyped(
    applicationImportance.map((importance) => {
      return { value: importance, name: appImportanceI18n[importance] }
    }),
    intl
  )
}

function normalizeSeverities(ruleSeverities: RuleSeverity[], intl) {
  return normalizeRuleSeveritiesToTypedVulnerabilities(
    ruleSeverities.map((severity) => {
      return { value: severity, name: ruleSeveritiesi18nConfig[severity] }
    }),
    intl
  )
}

export const VulnerabilityPolicyEditForm: React.FC<Props> = ({
  intl,
  organizationID,
  vulnerabilityPolicy,
  baseAssessRules,
  vulnAutoVerificationEnabled,
  updateVulnerabilityPolicy
}) => {
  const [name, setName] = React.useState(vulnerabilityPolicy.name)
  const [days, setDays] = React.useState(vulnerabilityPolicy.remediationDays)
  const [rules, setRules] = React.useState(
    normalizeAssessRulesToTypedVulnerabilities(vulnerabilityPolicy.rules)
  )
  const [ruleSeverities, setRuleSeverities] = React.useState(
    normalizeSeverities(vulnerabilityPolicy.ruleSeverities, intl)
  )
  const [applications, setApplications] = React.useState(
    normalizeApplicationsTyped(vulnerabilityPolicy.applications)
  )
  const [applicationImportance, setApplicationImportance] = React.useState(
    normalizeImportances(vulnerabilityPolicy.applicationImportance, intl)
  )
  const [serverEnvironments, setServerEnvironments] = React.useState(
    vulnerabilityPolicy.serverEnvironments
  )
  const [isDaysEnabled, setIsDaysEnabled] = React.useState(
    vulnerabilityPolicy.remediationDays > defaultDaysOnDisabled
  )

  const [autoVerificationType, setAutoVerificationType] = React.useState(
    vulnerabilityPolicy.autoVerificationType
  )

  const [isValidForm, setIsValidForm] = React.useState(false)

  return (
    <LegacyPolicyPanel
      children={
        <LegacyPolicyPanelBody>
          <React.Fragment>
            <PolicyManagementFormHeader
              i18nID={
                vulnerabilityPolicy.action === 'NOTIFY'
                  ? 'title_vulnerabilityPolicy_editViolationPolicy'
                  : 'title_vulnerabilityPolicy_editAutoVerificationPolicy'
              }
              Icon={IconRemediation}
              onClose={() =>
                routeToSourceNameGrid(
                  organizationID,
                  vulnerabilityPolicy.action
                )
              }
            />
            <VulnerabilityPolicyForm
              intl={intl}
              baseAssessRules={baseAssessRules}
              name={name}
              setName={setName}
              days={days}
              setDays={setDays}
              rules={rules}
              setRules={setRules}
              ruleSeverities={ruleSeverities}
              setRuleSeverities={setRuleSeverities}
              applications={applications}
              setApplications={setApplications}
              applicationImportance={applicationImportance}
              setApplicationImportance={setApplicationImportance}
              serverEnvironments={serverEnvironments}
              setServerEnvironments={setServerEnvironments}
              isDaysEnabled={isDaysEnabled}
              setIsDaysEnabled={setIsDaysEnabled}
              action={vulnerabilityPolicy.action}
              vulnAutoVerificationEnabled={vulnAutoVerificationEnabled}
              autoVerificationType={autoVerificationType}
              setAutoVerificationType={setAutoVerificationType}
              organizationID={organizationID}
              setIsValidForm={setIsValidForm}
            />
          </React.Fragment>
        </LegacyPolicyPanelBody>
      }
      outerFooter={
        <React.Fragment>
          <ContrastFormattedMessage
            id={'detail_vulnManagement_policy_updateSuccessMessage'}
            children={(successMessage: string) => (
              <ContrastFormattedMessage
                id={'detail_vulnManagement_policy_updateErrorMessage'}
                children={(errorMessage: string) => (
                  <ContrastFormattedMessage
                    id={'action_update'}
                    children={(saveMessage) => (
                      <ContrastFormattedMessage
                        id="action_cancel"
                        children={(cancelMessage) => (
                          <Fragment>
                            <Button
                              className={css`
                                margin-right: 20px;
                              `}
                              onClick={async () => {
                                await updateVulnerabilityPolicy(
                                  {
                                    organizationID,
                                    id: vulnerabilityPolicy.id,
                                    policy: {
                                      name,
                                      remediationDays: isDaysEnabled
                                        ? days
                                        : defaultDaysOnDisabled,
                                      allApplications: !(
                                        Array.isArray(applications) &&
                                        applications.length
                                      ),
                                      applicationImportance:
                                        applicationImportance.map(
                                          (importance) =>
                                            importance.id as ApplicationImportance
                                        ),
                                      applications: applications.map(
                                        (app) => app.id
                                      ),
                                      allRules: !(
                                        Array.isArray(rules) && rules.length
                                      ),
                                      ruleSeverities: ruleSeverities.map(
                                        (severity) =>
                                          severity.id as RuleSeverity
                                      ),
                                      rules: rules.map((rule) => rule.id),
                                      allServerEnvironments: !(
                                        Array.isArray(serverEnvironments) &&
                                        serverEnvironments.length
                                      ),
                                      serverEnvironments,
                                      routeBasedEnabled:
                                        vulnAutoVerificationEnabled
                                          ? vulnerabilityPolicy.routeBasedEnabled
                                          : false,
                                      action: vulnerabilityPolicy.action,
                                      autoVerificationType
                                    }
                                  },
                                  successMessage,
                                  errorMessage
                                )
                                routeToSourceNameGrid(
                                  organizationID,
                                  vulnerabilityPolicy.action
                                )
                              }}
                              isDisabled={!isValidForm}
                              testId={'update-vulnerability-policy-button'}
                            >
                              {saveMessage}
                            </Button>
                            <Button
                              variant="secondary"
                              className={css`
                                margin-right: 7px;
                              `}
                              onClick={() =>
                                routeToSourceNameGrid(
                                  organizationID,
                                  vulnerabilityPolicy.action
                                )
                              }
                              testId={
                                'update-vulnerability-policy-cancel-button'
                              }
                            >
                              {cancelMessage}
                            </Button>
                          </Fragment>
                        )}
                      />
                    )}
                  />
                )}
              />
            )}
          />
        </React.Fragment>
      }
    />
  )
}
